/**
 * Booking Database Context
 * 
 * Provides centralized state management for booking types and bookings
 * with session-based persistence.
 * 
 * @version 1.0.0
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  BookingTypeRecord,
  BookingRecord,
  BookingHistoryRecord,
  BookingConflictRecord,
  BookingTemplateRecord,
  BookingMetadata,
  BookingQueryOptions,
  BookingTypeQueryOptions,
  QueryResult,
  CreateBookingTypeInput,
  UpdateBookingTypeInput,
  CreateBookingInput,
  UpdateBookingInput,
  ValidationResult,
  STORAGE_KEYS,
} from '@/types/database';
import { 
  seedBookingTypes, 
  seedBookings, 
  seedBookingHistory, 
  seedBookingConflicts, 
  seedBookingTemplates 
} from '@/lib/database-seed-data';
import {
  ApiError,
  NetworkError,
  bookingTypesApi,
  bookingsApi,
  bookingHistoryApi,
  bookingConflictsApi,
  bookingTemplatesApi,
} from '@/services/api';
import { toast } from 'sonner';

// Helper function to handle API errors and show toast
const handleApiError = (error: unknown, operation: string): void => {
  console.error(`Error during ${operation}:`, error);
  
  if (error instanceof ApiError) {
    toast.error(`${operation} failed`, {
      description: error.getUserMessage(),
    });
  } else if (error instanceof NetworkError) {
    toast.error('Connection Error', {
      description: error.getUserMessage(),
    });
  } else if (error instanceof Error) {
    toast.error(`${operation} failed`, {
      description: error.message,
    });
  } else {
    toast.error(`${operation} failed`, {
      description: 'An unexpected error occurred. Please try again.',
    });
  }
};

// ============================================================================
// CONTEXT TYPE DEFINITION
// ============================================================================

interface BookingDatabaseContextType {
  // Booking Types
  bookingTypes: BookingTypeRecord[];
  getBookingType: (id: string) => BookingTypeRecord | undefined;
  createBookingType: (input: CreateBookingTypeInput) => Promise<BookingTypeRecord>;
  updateBookingType: (input: UpdateBookingTypeInput) => Promise<BookingTypeRecord>;
  deleteBookingType: (id: string) => Promise<boolean>;
  queryBookingTypes: (options?: BookingTypeQueryOptions) => BookingTypeRecord[];
  
  // Bookings
  bookings: BookingRecord[];
  getBooking: (id: string) => BookingRecord | undefined;
  createBooking: (input: CreateBookingInput) => Promise<BookingRecord>;
  updateBooking: (input: UpdateBookingInput) => Promise<BookingRecord>;
  deleteBooking: (id: string, soft?: boolean) => Promise<boolean>;
  restoreBooking: (id: string) => Promise<boolean>;
  queryBookings: (options?: BookingQueryOptions) => QueryResult<BookingRecord>;
  
  // Booking History
  bookingHistory: BookingHistoryRecord[];
  getBookingHistory: (bookingId: string) => BookingHistoryRecord[];
  
  // Booking Conflicts
  bookingConflicts: BookingConflictRecord[];
  detectConflicts: (bookingId: string) => BookingConflictRecord[];
  resolveConflict: (conflictId: string, resolutionType: string, notes?: string) => Promise<boolean>;
  
  // Booking Templates
  bookingTemplates: BookingTemplateRecord[];
  getBookingTemplate: (id: string) => BookingTemplateRecord | undefined;
  createBookingFromTemplate: (templateId: string, overrides: Partial<CreateBookingInput>) => Promise<BookingRecord>;
  
  // Metadata
  metadata: BookingMetadata;
  
  // Utility Functions
  initializeWithSeedData: () => void;
  clearAllData: () => void;
  exportData: () => string;
  importData: (jsonData: string) => boolean;
  
  // Loading State
  isLoading: boolean;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const BookingDatabaseContext = createContext<BookingDatabaseContextType | undefined>(undefined);

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateBookingType = (type: Partial<BookingTypeRecord>): ValidationResult => {
  const errors: string[] = [];
  
  if (!type.code || type.code.length < 2) {
    errors.push('Code must be at least 2 characters');
  }
  
  if (!type.name || type.name.length < 3) {
    errors.push('Name must be at least 3 characters');
  }
  
  if (type.sortOrder !== undefined && type.sortOrder < 0) {
    errors.push('Sort order must be non-negative');
  }
  
  return { isValid: errors.length === 0, errors };
};

const validateBooking = (booking: Partial<BookingRecord>): ValidationResult => {
  const errors: string[] = [];
  
  if (booking.startTime && booking.endTime) {
    const start = new Date(booking.startTime);
    const end = new Date(booking.endTime);
    
    if (start >= end) {
      errors.push('End date/time must be after start date/time');
    }
  }
  
  if (!booking.resourceId && (!booking.resourceIds || booking.resourceIds.length === 0)) {
    errors.push('At least one resource must be assigned');
  }
  
  if (!booking.projectId) {
    errors.push('Project is required');
  }
  
  if (!booking.title || booking.title.length < 3) {
    errors.push('Title must be at least 3 characters');
  }
  
  return { isValid: errors.length === 0, errors };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const generateId = (prefix: string): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const calculateDuration = (startTime: string, endTime: string) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  const days = hours / 8; // Assuming 8-hour workday
  return { hours, days };
};

const getCurrentUser = (): string => {
  // In a real app, this would get the current user from auth context
  return 'current-user';
};

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = generateId('session');
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function BookingDatabaseProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [bookingTypes, setBookingTypes] = useState<BookingTypeRecord[]>([]);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [bookingHistory, setBookingHistory] = useState<BookingHistoryRecord[]>([]);
  const [bookingConflicts, setBookingConflicts] = useState<BookingConflictRecord[]>([]);
  const [bookingTemplates, setBookingTemplates] = useState<BookingTemplateRecord[]>([]);
  const [metadata, setMetadata] = useState<BookingMetadata>({
    version: '1.0.0',
    lastSyncAt: new Date().toISOString(),
    totalBookings: 0,
    totalBookingTypes: 0,
    sessionId: getSessionId(),
    userId: getCurrentUser(),
    createdAt: new Date().toISOString(),
  });

  // ============================================================================
  // LOAD DATA FROM SESSION STORAGE
  // ============================================================================

  useEffect(() => {
    try {
      // Load booking types
      const storedTypes = sessionStorage.getItem(STORAGE_KEYS.BOOKING_TYPES);
      if (storedTypes) {
        setBookingTypes(JSON.parse(storedTypes));
      } else {
        // Initialize with seed data if no data exists
        setBookingTypes(seedBookingTypes);
      }

      // Load bookings
      const storedBookings = sessionStorage.getItem(STORAGE_KEYS.BOOKINGS);
      if (storedBookings) {
        setBookings(JSON.parse(storedBookings));
      } else {
        setBookings(seedBookings);
      }

      // Load booking history
      const storedHistory = sessionStorage.getItem(STORAGE_KEYS.BOOKING_HISTORY);
      if (storedHistory) {
        setBookingHistory(JSON.parse(storedHistory));
      } else {
        setBookingHistory(seedBookingHistory);
      }

      // Load booking conflicts
      const storedConflicts = sessionStorage.getItem(STORAGE_KEYS.BOOKING_CONFLICTS);
      if (storedConflicts) {
        setBookingConflicts(JSON.parse(storedConflicts));
      } else {
        setBookingConflicts(seedBookingConflicts);
      }

      // Load booking templates
      const storedTemplates = sessionStorage.getItem(STORAGE_KEYS.BOOKING_TEMPLATES);
      if (storedTemplates) {
        setBookingTemplates(JSON.parse(storedTemplates));
      } else {
        setBookingTemplates(seedBookingTemplates);
      }

      // Load metadata
      const storedMetadata = sessionStorage.getItem(STORAGE_KEYS.METADATA);
      if (storedMetadata) {
        setMetadata(JSON.parse(storedMetadata));
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data from session storage:', error);
      setIsLoading(false);
    }
  }, []);

  // ============================================================================
  // PERSIST DATA TO SESSION STORAGE
  // ============================================================================

  useEffect(() => {
    if (!isLoading) {
      try {
        sessionStorage.setItem(STORAGE_KEYS.BOOKING_TYPES, JSON.stringify(bookingTypes));
        sessionStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(bookings));
        sessionStorage.setItem(STORAGE_KEYS.BOOKING_HISTORY, JSON.stringify(bookingHistory));
        sessionStorage.setItem(STORAGE_KEYS.BOOKING_CONFLICTS, JSON.stringify(bookingConflicts));
        sessionStorage.setItem(STORAGE_KEYS.BOOKING_TEMPLATES, JSON.stringify(bookingTemplates));
        
        // Update metadata
        const updatedMetadata: BookingMetadata = {
          ...metadata,
          lastSyncAt: new Date().toISOString(),
          totalBookings: bookings.filter(b => !b.isDeleted).length,
          totalBookingTypes: bookingTypes.filter(bt => bt.active).length,
        };
        setMetadata(updatedMetadata);
        sessionStorage.setItem(STORAGE_KEYS.METADATA, JSON.stringify(updatedMetadata));
      } catch (error) {
        console.error('Error saving data to session storage:', error);
      }
    }
  }, [bookingTypes, bookings, bookingHistory, bookingConflicts, bookingTemplates, isLoading]);

  // ============================================================================
  // BOOKING TYPE OPERATIONS
  // ============================================================================

  const getBookingType = useCallback((id: string): BookingTypeRecord | undefined => {
    return bookingTypes.find(bt => String(bt.id) === String(id));
  }, [bookingTypes]);

  const createBookingType = useCallback(async (input: CreateBookingTypeInput): Promise<BookingTypeRecord> => {
    const validation = validateBookingType(input);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();
    
    const newBookingType: BookingTypeRecord = {
      ...input,
      id: generateId('bt'),
      createdAt: now,
      createdBy: currentUser,
      updatedAt: now,
      updatedBy: currentUser,
      version: 1,
      sessionId: getSessionId(),
    };

    setBookingTypes(prev => [...prev, newBookingType]);
    return newBookingType;
  }, []);

  const updateBookingType = useCallback(async (input: UpdateBookingTypeInput): Promise<BookingTypeRecord> => {
    const existing = bookingTypes.find(bt => String(bt.id) === String(input.id));
    if (!existing) {
      throw new Error(`Booking type not found: ${input.id}`);
    }

    if (existing.version !== input.version) {
      throw new Error('Version mismatch - data has been modified by another user');
    }

    const validation = validateBookingType({ ...existing, ...input });
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();
    
    const updatedBookingType: BookingTypeRecord = {
      ...existing,
      ...input,
      updatedAt: now,
      updatedBy: currentUser,
      version: existing.version + 1,
    };

    setBookingTypes(prev => prev.map(bt => String(bt.id) === String(input.id) ? updatedBookingType : bt));
    return updatedBookingType;
  }, [bookingTypes]);

  const deleteBookingType = useCallback(async (id: string): Promise<boolean> => {
    // Check if any bookings use this type
    const hasBookings = bookings.some(b => String(b.bookingTypeId) === String(id) && !b.isDeleted);
    if (hasBookings) {
      throw new Error('Cannot delete booking type that has associated bookings');
    }

    setBookingTypes(prev => prev.filter(bt => String(bt.id) !== String(id)));
    return true;
  }, [bookings]);

  const queryBookingTypes = useCallback((options?: BookingTypeQueryOptions): BookingTypeRecord[] => {
    let result = [...bookingTypes];

    if (options?.activeOnly) {
      result = result.filter(bt => bt.active);
    }

    if (options?.categories && options.categories.length > 0) {
      result = result.filter(bt => options.categories!.includes(bt.category));
    }

    if (options?.searchTerm) {
      const term = options.searchTerm.toLowerCase();
      result = result.filter(bt => 
        bt.name.toLowerCase().includes(term) ||
        bt.code.toLowerCase().includes(term) ||
        bt.description?.toLowerCase().includes(term)
      );
    }

    // Sort
    if (options?.sortBy) {
      result.sort((a, b) => {
        const aVal = a[options.sortBy!];
        const bVal = b[options.sortBy!];
        const order = options.sortOrder === 'desc' ? -1 : 1;
        if (aVal === undefined || bVal === undefined) return 0;
        return aVal < bVal ? -order : aVal > bVal ? order : 0;
      });
    } else {
      // Default sort by sortOrder
      result.sort((a, b) => a.sortOrder - b.sortOrder);
    }

    return result;
  }, [bookingTypes]);

  // ============================================================================
  // BOOKING OPERATIONS
  // ============================================================================

  const getBooking = useCallback((id: string): BookingRecord | undefined => {
    return bookings.find(b => String(b.id) === String(id));
  }, [bookings]);

  const createBooking = useCallback(async (input: CreateBookingInput): Promise<BookingRecord> => {
    const validation = validateBooking(input);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();
    const duration = calculateDuration(input.startTime, input.endTime);
    
    const newBooking: BookingRecord = {
      ...input,
      id: generateId('booking'),
      durationHours: duration.hours,
      durationDays: duration.days,
      createdAt: now,
      createdBy: currentUser,
      updatedAt: now,
      updatedBy: currentUser,
      version: 1,
      sessionId: getSessionId(),
      isDraft: input.isDraft ?? false,
      isDeleted: false,
    };

    setBookings(prev => [...prev, newBooking]);

    // Create history record
    const historyRecord: BookingHistoryRecord = {
      id: generateId('history'),
      bookingId: newBooking.id,
      changeType: 'created',
      changedFields: [],
      newState: newBooking,
      changedAt: now,
      changedBy: currentUser,
      changedByName: 'Current User', // Would come from user context
      sessionId: getSessionId(),
    };
    setBookingHistory(prev => [...prev, historyRecord]);

    return newBooking;
  }, []);

  const updateBooking = useCallback(async (input: UpdateBookingInput): Promise<BookingRecord> => {
    const existing = bookings.find(b => String(b.id) === String(input.id));
    if (!existing) {
      throw new Error(`Booking not found: ${input.id}`);
    }

    if (existing.version !== input.version) {
      throw new Error('Version mismatch - data has been modified by another user');
    }

    const validation = validateBooking({ ...existing, ...input });
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();
    
    let duration = { hours: existing.durationHours, days: existing.durationDays };
    if (input.startTime || input.endTime) {
      const startTime = input.startTime || existing.startTime;
      const endTime = input.endTime || existing.endTime;
      duration = calculateDuration(startTime, endTime);
    }
    
    const updatedBooking: BookingRecord = {
      ...existing,
      ...input,
      durationHours: duration.hours,
      durationDays: duration.days,
      updatedAt: now,
      updatedBy: currentUser,
      version: existing.version + 1,
    };

    setBookings(prev => prev.map(b => String(b.id) === String(input.id) ? updatedBooking : b));

    // Create history record
    const changedFields = Object.keys(input).filter(key => key !== 'id' && key !== 'version');
    const historyRecord: BookingHistoryRecord = {
      id: generateId('history'),
      bookingId: updatedBooking.id,
      changeType: 'updated',
      changedFields: changedFields.map(field => ({
        fieldName: field,
        oldValue: (existing as any)[field],
        newValue: (input as any)[field],
        dataType: typeof (input as any)[field],
      })),
      previousState: existing,
      newState: updatedBooking,
      changedAt: now,
      changedBy: currentUser,
      changedByName: 'Current User',
      sessionId: getSessionId(),
    };
    setBookingHistory(prev => [...prev, historyRecord]);

    return updatedBooking;
  }, [bookings]);

  const deleteBooking = useCallback(async (id: string, soft: boolean = true): Promise<boolean> => {
    const existing = bookings.find(b => String(b.id) === String(id));
    if (!existing) {
      throw new Error(`Booking not found: ${id}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();

    if (soft) {
      // Soft delete
      const deletedBooking: BookingRecord = {
        ...existing,
        isDeleted: true,
        deletedAt: now,
        deletedBy: currentUser,
        updatedAt: now,
        updatedBy: currentUser,
        version: existing.version + 1,
      };
      setBookings(prev => prev.map(b => String(b.id) === String(id) ? deletedBooking : b));

      // Create history record
      const historyRecord: BookingHistoryRecord = {
        id: generateId('history'),
        bookingId: id,
        changeType: 'deleted',
        changedFields: [
          { fieldName: 'isDeleted', oldValue: false, newValue: true, dataType: 'boolean' },
        ],
        previousState: existing,
        newState: deletedBooking,
        changedAt: now,
        changedBy: currentUser,
        changedByName: 'Current User',
        sessionId: getSessionId(),
      };
      setBookingHistory(prev => [...prev, historyRecord]);
    } else {
      // Hard delete
      setBookings(prev => prev.filter(b => String(b.id) !== String(id)));
    }

    return true;
  }, [bookings]);

  const restoreBooking = useCallback(async (id: string): Promise<boolean> => {
    const existing = bookings.find(b => String(b.id) === String(id));
    if (!existing || !existing.isDeleted) {
      throw new Error(`Deleted booking not found: ${id}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();

    const restoredBooking: BookingRecord = {
      ...existing,
      isDeleted: false,
      deletedAt: undefined,
      deletedBy: undefined,
      updatedAt: now,
      updatedBy: currentUser,
      version: existing.version + 1,
    };
    setBookings(prev => prev.map(b => String(b.id) === String(id) ? restoredBooking : b));

    // Create history record
    const historyRecord: BookingHistoryRecord = {
      id: generateId('history'),
      bookingId: id,
      changeType: 'restored',
      changedFields: [
        { fieldName: 'isDeleted', oldValue: true, newValue: false, dataType: 'boolean' },
      ],
      previousState: existing,
      newState: restoredBooking,
      changedAt: now,
      changedBy: currentUser,
      changedByName: 'Current User',
      sessionId: getSessionId(),
    };
    setBookingHistory(prev => [...prev, historyRecord]);

    return true;
  }, [bookings]);

  const queryBookings = useCallback((options?: BookingQueryOptions): QueryResult<BookingRecord> => {
    let result = [...bookings];

    // Filter by flags
    if (!options?.includeDrafts) {
      result = result.filter(b => !b.isDraft);
    }
    if (!options?.includeDeleted) {
      result = result.filter(b => !b.isDeleted);
    }

    // Filter by booking type
    if (options?.bookingTypeIds && options.bookingTypeIds.length > 0) {
      result = result.filter(b => options.bookingTypeIds!.includes(b.bookingTypeId));
    }

    // Filter by resource
    if (options?.resourceIds && options.resourceIds.length > 0) {
      result = result.filter(b => 
        options.resourceIds!.includes(b.resourceId) ||
        (b.resourceIds && b.resourceIds.some(rid => options.resourceIds!.includes(rid)))
      );
    }

    // Filter by project
    if (options?.projectIds && options.projectIds.length > 0) {
      result = result.filter(b => options.projectIds!.includes(b.projectId));
    }

    // Filter by status
    if (options?.statuses && options.statuses.length > 0) {
      result = result.filter(b => options.statuses!.includes(b.status));
    }

    // Filter by priority
    if (options?.priorities && options.priorities.length > 0) {
      result = result.filter(b => options.priorities!.includes(b.priority));
    }

    // Filter by date range
    if (options?.startDate) {
      result = result.filter(b => b.startTime >= options.startDate!);
    }
    if (options?.endDate) {
      result = result.filter(b => b.endTime <= options.endDate!);
    }

    // Search
    if (options?.searchTerm) {
      const term = options.searchTerm.toLowerCase();
      result = result.filter(b => 
        b.title.toLowerCase().includes(term) ||
        b.description?.toLowerCase().includes(term) ||
        b.projectName.toLowerCase().includes(term)
      );
    }

    // Sort
    if (options?.sortBy) {
      result.sort((a, b) => {
        const aVal = a[options.sortBy!];
        const bVal = b[options.sortBy!];
        const order = options.sortOrder === 'desc' ? -1 : 1;
        if (aVal === undefined || bVal === undefined) return 0;
        return aVal < bVal ? -order : aVal > bVal ? order : 0;
      });
    } else {
      // Default sort by start time
      result.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    }

    // Pagination
    const page = options?.page || 1;
    const pageSize = options?.pageSize || 50;
    const total = result.length;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paginatedResult = result.slice(start, end);

    return {
      data: paginatedResult,
      total,
      page,
      pageSize,
      hasMore: end < total,
    };
  }, [bookings]);

  // ============================================================================
  // BOOKING HISTORY OPERATIONS
  // ============================================================================

  const getBookingHistory = useCallback((bookingId: string): BookingHistoryRecord[] => {
    return bookingHistory
      .filter(h => String(h.bookingId) === String(bookingId))
      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
  }, [bookingHistory]);

  // ============================================================================
  // BOOKING CONFLICT OPERATIONS
  // ============================================================================

  const detectConflicts = useCallback((bookingId: string): BookingConflictRecord[] => {
    return bookingConflicts.filter(c => 
      (String(c.bookingId1) === String(bookingId) || String(c.bookingId2) === String(bookingId)) && !c.isResolved
    );
  }, [bookingConflicts]);

  const resolveConflict = useCallback(async (
    conflictId: string, 
    resolutionType: string, 
    notes?: string
  ): Promise<boolean> => {
    const conflict = bookingConflicts.find(c => String(c.id) === String(conflictId));
    if (!conflict) {
      throw new Error(`Conflict not found: ${conflictId}`);
    }

    const now = new Date().toISOString();
    const currentUser = getCurrentUser();

    const resolvedConflict: BookingConflictRecord = {
      ...conflict,
      isResolved: true,
      resolutionType: resolutionType as any,
      resolvedAt: now,
      resolvedBy: currentUser,
      resolutionNotes: notes,
    };

    setBookingConflicts(prev => prev.map(c => String(c.id) === String(conflictId) ? resolvedConflict : c));
    return true;
  }, [bookingConflicts]);

  // ============================================================================
  // BOOKING TEMPLATE OPERATIONS
  // ============================================================================

  const getBookingTemplate = useCallback((id: string): BookingTemplateRecord | undefined => {
    return bookingTemplates.find(t => String(t.id) === String(id));
  }, [bookingTemplates]);

  const createBookingFromTemplate = useCallback(async (
    templateId: string,
    overrides: Partial<CreateBookingInput>
  ): Promise<BookingRecord> => {
    const template = bookingTemplates.find(t => String(t.id) === String(templateId));
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }

    const bookingInput: CreateBookingInput = {
      ...template.templateData,
      ...overrides,
    } as CreateBookingInput;

    const booking = await createBooking(bookingInput);

    // Update template usage
    const updatedTemplate: BookingTemplateRecord = {
      ...template,
      usageCount: template.usageCount + 1,
      lastUsedAt: new Date().toISOString(),
    };
    setBookingTemplates(prev => prev.map(t => String(t.id) === String(templateId) ? updatedTemplate : t));

    return booking;
  }, [bookingTemplates, createBooking]);

  // ============================================================================
  // UTILITY OPERATIONS
  // ============================================================================

  const initializeWithSeedData = useCallback(() => {
    setBookingTypes(seedBookingTypes);
    setBookings(seedBookings);
    setBookingHistory(seedBookingHistory);
    setBookingConflicts(seedBookingConflicts);
    setBookingTemplates(seedBookingTemplates);
  }, []);

  const clearAllData = useCallback(() => {
    setBookingTypes([]);
    setBookings([]);
    setBookingHistory([]);
    setBookingConflicts([]);
    setBookingTemplates([]);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_TYPES);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKINGS);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_HISTORY);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_CONFLICTS);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_TEMPLATES);
    sessionStorage.removeItem(STORAGE_KEYS.METADATA);
  }, []);

  const exportData = useCallback((): string => {
    const data = {
      bookingTypes,
      bookings,
      bookingHistory,
      bookingConflicts,
      bookingTemplates,
      metadata,
    };
    return JSON.stringify(data, null, 2);
  }, [bookingTypes, bookings, bookingHistory, bookingConflicts, bookingTemplates, metadata]);

  const importData = useCallback((jsonData: string): boolean => {
    try {
      const data = JSON.parse(jsonData);
      if (data.bookingTypes) setBookingTypes(data.bookingTypes);
      if (data.bookings) setBookings(data.bookings);
      if (data.bookingHistory) setBookingHistory(data.bookingHistory);
      if (data.bookingConflicts) setBookingConflicts(data.bookingConflicts);
      if (data.bookingTemplates) setBookingTemplates(data.bookingTemplates);
      if (data.metadata) setMetadata(data.metadata);
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: BookingDatabaseContextType = {
    // Booking Types
    bookingTypes,
    getBookingType,
    createBookingType,
    updateBookingType,
    deleteBookingType,
    queryBookingTypes,
    
    // Bookings
    bookings,
    getBooking,
    createBooking,
    updateBooking,
    deleteBooking,
    restoreBooking,
    queryBookings,
    
    // Booking History
    bookingHistory,
    getBookingHistory,
    
    // Booking Conflicts
    bookingConflicts,
    detectConflicts,
    resolveConflict,
    
    // Booking Templates
    bookingTemplates,
    getBookingTemplate,
    createBookingFromTemplate,
    
    // Metadata
    metadata,
    
    // Utility Functions
    initializeWithSeedData,
    clearAllData,
    exportData,
    importData,
    
    // Loading State
    isLoading,
  };

  return (
    <BookingDatabaseContext.Provider value={value}>
      {children}
    </BookingDatabaseContext.Provider>
  );
}

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export function useBookingDatabase() {
  const context = useContext(BookingDatabaseContext);
  if (context === undefined) {
    throw new Error('useBookingDatabase must be used within a BookingDatabaseProvider');
  }
  return context;
}
